import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Copy, 
  Check, 
  Twitter, 
  Linkedin, 
  Facebook, 
  Tag,
  Heart,
  Users
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface ShareableContent {
  id: string;
  title: string;
  description: string;
  url: string;
  category?: string;
  upvotes?: number;
  image?: string;
  type?: 'tool' | 'survey' | 'template' | 'article';
}

interface BaseShareDialogProps {
  content: ShareableContent;
  isOpen: boolean;
  onClose: () => void;
  onTrackShare: (platform: string) => void;
  customShareText?: string;
  showQRCode?: boolean;
  showMobileShare?: boolean;
  showEmailShare?: boolean;
}

export const BaseShareDialog: React.FC<BaseShareDialogProps> = ({ 
  content, 
  isOpen, 
  onClose, 
  onTrackShare,
  customShareText,
  showQRCode = true,
  showMobileShare = true,
  showEmailShare = true
}) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content.url);
      setCopied(true);
      onTrackShare('copy_link');
      toast({
        title: "Link Copied!",
        description: "Link has been copied to clipboard.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleSocialShare = (platform: string) => {
    const text = customShareText || `Check out this ${content.type || 'content'}: ${content.title}`;
    const encodedText = encodeURIComponent(text);
    const encodedUrl = encodeURIComponent(content.url);
    
    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'reddit':
        shareUrl = `https://reddit.com/submit?url=${encodedUrl}&title=${encodeURIComponent(content.title)}`;
        break;
      case 'slack':
        shareUrl = `https://slack.com/intl/en/help/articles/360000561048-How-to-share-links-in-Slack`;
        // For Slack, we'll just copy the link since direct sharing isn't supported
        handleCopy();
        return;
    }
    
    if (shareUrl) {
      onTrackShare(platform);
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  const handleEmailShare = () => {
    if (!showEmailShare) return;
    
    onTrackShare('email');
    const subject = encodeURIComponent(`Check out this ${content.type || 'content'}: ${content.title}`);
    const body = encodeURIComponent(`I found this amazing ${content.type || 'content'} that might be useful:\n\n${content.title}\n${content.description}\n\nCheck it out: ${content.url}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleMobileShare = async () => {
    if (!showMobileShare) return;
    
    if (navigator.share) {
      try {
        onTrackShare('mobile_native');
        await navigator.share({
          title: content.title,
          text: content.description,
          url: content.url,
        });
      } catch (error) {
        console.error('Error sharing:', error);
        // Fallback to copy link
        handleCopy();
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      handleCopy();
    }
  };

  const generateQRCode = () => {
    if (!showQRCode) return;
    
    onTrackShare('qr_code');
    // Simple QR code generation using a service
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(content.url)}`;
    window.open(qrUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">Share {content.title}</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-xl">
            ×
          </Button>
        </div>
        
        <div className="space-y-6">
          {/* Content Preview Card */}
          <div className="p-4 bg-gradient-subtle rounded-lg border border-primary/10">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1">{content.title}</h4>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{content.description}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {content.category && (
                    <span className="flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {content.category}
                    </span>
                  )}
                  {content.upvotes && (
                    <span className="flex items-center gap-1">
                      <Heart className="w-3 h-3" />
                      {content.upvotes} upvotes
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        
          
          {/* Other Share Options */}
          <div>
            <h4 className="font-medium mb-3">Share via</h4>
            <div className="space-y-2">
                <Button
                variant="outline"
                onClick={() => handleSocialShare('linkedin')}
                 className="w-full justify-start gap-2"
              >
                <Linkedin className="w-5 h-5 text-blue-600" />
                <span>LinkedIn</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSocialShare('reddit')}
                className="w-full justify-start gap-2"
              >
                <div className="w-5 h-5 bg-orange-500 rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">R</span>
                </div>
                <span>Reddit</span>
              </Button>
              {showEmailShare && (
                <Button
                  variant="outline"
                  onClick={handleEmailShare}
                  className="w-full justify-start gap-2"
                >
                  <div className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center">
                    <span className="text-white text-xs">✉</span>
                  </div>
                  <span>Share via Email</span>
                </Button>
              )}
              
              {showMobileShare && (
                <Button
                  variant="outline"
                  onClick={handleMobileShare}
                  className="w-full justify-start gap-2"
                >
                  <div className="w-5 h-5 bg-purple-500 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">S</span>
                  </div>
                  <span>Share via Mobile</span>
                </Button>
              )}

              {showQRCode && (
                <Button
                  variant="outline"
                  onClick={generateQRCode}
                  className="w-full justify-start gap-2"
                >
                  <div className="w-5 h-5 bg-green-500 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">Q</span>
                  </div>
                  <span>Generate QR Code</span>
                </Button>
              )}
            </div>
          </div>
          
          {/* Copy Link Section */}
          <div className="space-y-3">
            <h4 className="font-medium">Or copy the link directly</h4>
            <div className="flex gap-2">
              <input
                type="text"
                value={content.url}
                readOnly
                className="flex-1 px-3 py-2 text-sm border rounded-md bg-muted font-mono text-xs"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="min-w-[100px]"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>
          </div>
          
          {/* Preview Info */}
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-2 text-sm">Preview when shared</h4>
            <p className="text-xs text-muted-foreground mb-3">
              When you share this link on social media, it will show a rich preview with the content's information, 
              description, and metadata.
            </p>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Title:</span>
                <span className="font-medium">{content.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Description:</span>
                <span className="font-medium line-clamp-1 max-w-[200px]">{content.description}</span>
              </div>
              {content.category && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Category:</span>
                  <span className="font-medium">{content.category}</span>
                </div>
              )}
              {content.upvotes && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Upvotes:</span>
                  <span className="font-medium">{content.upvotes}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 